import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, Ban, Target as TargetIcon, Octagon, LogIn, Copy, Check, AlertTriangle } from "lucide-react";
import ChartViewer from "@/components/market/ChartViewer";
import { apiMarketCandles } from "@/lib/api";

/**
 * MarketResultSimple
 * ──────────────────────────────────────────────────────────────────────
 * Beginner-focused result screen for /market. V2 trade logic only.
 *
 * Sections (and ONLY these):
 *   1. Pair · timeframe · live badge · reset
 *   2. Direction + Certainty hero
 *   3. Compact chart with the plan drawn on it (entry zone, stop, TPs)
 *      — only when there IS a trade. Omitted on NO TRADE.
 *   4. Preferred Trade Idea card (Entry · Targets · Stop · R:R · reason)
 *      OR NO TRADE amber card with one short sentence
 *
 * Everything else (setup status, TF reliability, scenario commentary,
 * S/R section, alternative entry, short-term trade, analysis inputs)
 * stays on the `result` object and is not rendered here.
 */
export default function MarketResultSimple({ result, marketContext = null, onReset, resetLabel = "Back to chart" }) {
  const rawStatus = (result?.setup_status || "").toUpperCase().trim();
  // Defensive mirror of the backend rule Stop < Entry ≤ Price < TP1 < TP2 < TP3.
  const builtPlan = useMemo(() => buildPlanFromResult(result), [result]);
  const planInvalid = !!result?.preferred_trade_idea && rawStatus !== "NO TRADE" && !isValidLongPlan(builtPlan, result?.reference_price);
  const noTrade = !result?.preferred_trade_idea || rawStatus === "NO TRADE" || planInvalid;
  const direction = noTrade ? "NO TRADE" : (result?.preferred_trade_idea?.direction?.toUpperCase() || "LONG");
  const certainty = typeof result?.confidence_score === "number" ? result.confidence_score : null;

  // Map legacy status strings to the current V2 labels — the engine
  // currently emits "READY" / "WAITING FOR CONFIRMATION" / "WAITING FOR ENTRY"
  // but older rows in history may still carry "LONG READY" / "WAIT FOR CONFIRMATION".
  const status = normalizeStatus(rawStatus, noTrade);

  const pair = marketContext?.symbol || result?.asset_guess || "";
  const tf = marketContext?.timeframe || result?.timeframe_guess || "";
  const exchange = marketContext?.exchange || "binance";
  const marketType = marketContext?.market_type || "spot";

  // Numeric plan derived from the AI's string fields — used for chart overlay + share text.
  const plan = noTrade ? null : builtPlan;

  return (
    <section className="max-w-2xl mx-auto px-1 sm:px-0" data-testid="market-result-simple">
      {/* Top row: pair · timeframe · reset */}
      <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-3 mb-5">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {pair && (
            <span className="font-mono text-sm uppercase tracking-[0.2em] text-neutral-300" data-testid="result-pair">
              {pair}
            </span>
          )}
          {tf && (
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 border border-neutral-800 px-2 py-1" data-testid="result-tf">
              {tf}
            </span>
          )}
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00E5FF] border border-[#00E5FF]/30 bg-[#00E5FF]/[0.05] px-2 py-1">
            live market
          </span>
        </div>
        {onReset && (
          <button
            onClick={onReset}
            data-testid="result-reset"
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {resetLabel}
          </button>
        )}
      </div>

      {/* Direction + Certainty hero */}
      <DirectionHero direction={direction} certainty={certainty} statusLabel={status.label} statusAccent={status.accent} />

      {/* Future-entry hint — only when WAITING FOR ENTRY */}
      {status.key === "WAITING_FOR_ENTRY" && !noTrade && (
        <FutureEntryHint planned={result?.preferred_trade_idea?.preferred_entry?.entry_zone} />
      )}

      {/* Volatility / stop warning — surfaced by the backend when ATR
          would push the tactical stop beyond the 2% cap. */}
      <WarningBanner warnings={result?.warnings} />

      {/* Compact chart with plan overlay — trade path only */}
      {!noTrade && plan && pair && tf && (
        <PlanChart exchange={exchange} marketType={marketType} symbol={pair} timeframe={tf} plan={plan} />
      )}

      {noTrade ? (
        <NoTradeCard reason={planInvalid ? INVALID_PLAN_REASON : result?.no_trade_reason} />
      ) : (
        <TradeIdeaCard result={result} plan={plan} pair={pair} tf={tf} statusLabel={status.label} />
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Status mapping — one source of truth for all 4 V2 statuses          */
/* ────────────────────────────────────────────────────────────────── */
function normalizeStatus(raw, noTrade) {
  if (noTrade) return { key: "NO_TRADE", label: "NO TRADE", accent: "#FFB000" };
  const r = (raw || "").toUpperCase().trim();
  // Legacy aliases from older history rows:
  if (r === "LONG READY" || r === "READY") {
    return { key: "READY", label: "READY", accent: "#00FF66" };
  }
  if (r === "WAITING FOR ENTRY") {
    return { key: "WAITING_FOR_ENTRY", label: "WAITING FOR ENTRY", accent: "#FFB000" };
  }
  if (r === "WAITING FOR CONFIRMATION" || r === "WAIT FOR CONFIRMATION") {
    return { key: "WAITING_FOR_CONFIRMATION", label: "WAITING FOR CONFIRMATION", accent: "#FFB000" };
  }
  // Unknown status with an active trade idea — treat as "READY" neutral.
  return { key: "READY", label: r || "READY", accent: "#00FF66" };
}

/* ────────────────────────────────────────────────────────────────── */
/* Future-entry hint banner                                             */
/* ────────────────────────────────────────────────────────────────── */
function FutureEntryHint({ planned }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="border border-[#FFB000]/35 bg-[#FFB000]/[0.04] px-4 py-3 mb-5 flex items-start gap-3"
      data-testid="future-entry-hint"
    >
      <AlertTriangle className="w-4 h-4 text-[#FFB000] flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#FFB000] font-bold mb-1">
          Not Active Yet
        </div>
        <p className="text-xs sm:text-sm text-neutral-200 leading-relaxed">
          Price must come down to {planned ? <span className="font-mono text-white">{planned}</span> : "the planned entry"} before this setup becomes actionable. Don't chase the current price — wait for the market to reach the planned entry.
        </p>
      </div>
    </motion.div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Direction + certainty                                              */
/* ────────────────────────────────────────────────────────────────── */
function DirectionHero({ direction, certainty, statusLabel, statusAccent }) {
  const isLong = direction === "LONG";
  const isNoTrade = direction === "NO TRADE";
  const accent = isLong ? "#00FF66" : isNoTrade ? "#FFB000" : "#FFFFFF";
  const Icon = isLong ? TrendingUp : isNoTrade ? Ban : TrendingUp;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="border border-neutral-900 bg-neutral-950 p-5 sm:p-6 mb-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-2">direction</div>
          <div className="flex items-center gap-2.5 sm:gap-3" data-testid="direction-badge">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: accent }} />
            <span className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter" style={{ color: accent }}>
              {direction}
            </span>
          </div>
          {/* Setup status pill — only shown when we have one and it's not plain "READY" for a LONG */}
          {statusLabel && !isNoTrade && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border"
              style={{ color: statusAccent, borderColor: `${statusAccent}55`, background: `${statusAccent}10` }}
              data-testid="status-badge"
            >
              {statusLabel}
            </div>
          )}
        </div>
        {certainty !== null && (
          <div className="sm:text-right" data-testid="certainty-block">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-2">certainty</div>
            <div className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter text-white font-mono">
              {certainty}%
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Compact chart with plan overlay                                     */
/* ────────────────────────────────────────────────────────────────── */
function PlanChart({ exchange, marketType, symbol, timeframe, plan }) {
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 300 candles is plenty of context for a beginner glance.
        const rows = await apiMarketCandles({ exchange, market_type: marketType, symbol, timeframe, limit: 300 });
        if (cancelled) return;
        // API returns { candles: [...], ... }
        setCandles(Array.isArray(rows) ? rows : (rows?.candles || []));
      } catch (e) {
        if (!cancelled) setError(e?.message || "chart unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [exchange, marketType, symbol, timeframe]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: 0.03 }}
      className="border border-neutral-900 bg-neutral-950 mb-5 overflow-hidden"
      data-testid="plan-chart"
    >
      <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-900">
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">plan on chart</span>
        <div className="hidden sm:flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.2em]">
          <Dot color="#00E5FF" /> <span className="text-neutral-400">entry</span>
          <Dot color="#FF4757" /> <span className="text-neutral-400">stop</span>
          <Dot color="#00FF66" /> <span className="text-neutral-400">targets</span>
        </div>
      </div>
      <div className="hidden sm:block">
        <ChartViewer candles={candles} loading={loading} error={error} height={280} plan={plan} />
      </div>
      <div className="sm:hidden">
        <ChartViewer candles={candles} loading={loading} error={error} height={220} plan={plan} />
      </div>
    </motion.div>
  );
}

function Dot({ color }) {
  return <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />;
}

/* ────────────────────────────────────────────────────────────────── */
/* Warning banner (High Stop-Loss Risk etc.)                           */
/* ────────────────────────────────────────────────────────────────── */
function WarningBanner({ warnings }) {
  if (!Array.isArray(warnings) || warnings.length === 0) return null;
  return (
    <div className="space-y-3 mb-5" data-testid="warnings-list">
      {warnings.map((w, i) => (
        <motion.div
          key={w.code || i}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.02 * i }}
          className="border border-[#FF4757]/40 bg-[#FF4757]/[0.05] px-4 py-3 flex items-start gap-3"
          data-testid={`warning-${w.code || i}`}
        >
          <AlertTriangle className="w-4 h-4 text-[#FF4757] flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#FF4757] font-bold mb-1">
              {w.title}
            </div>
            <p className="text-xs sm:text-sm text-neutral-200 leading-relaxed">{w.message}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Preferred Trade Idea card                                          */
/* ────────────────────────────────────────────────────────────────── */
function TradeIdeaCard({ result, plan, pair, tf, statusLabel }) {
  const pref = result.preferred_trade_idea;
  const pe = pref?.preferred_entry || {};
  const targets = Array.isArray(pref?.targets) ? pref.targets : null;
  const useLadder = targets && targets.length >= 2;
  const [copied, setCopied] = useState(false);

  const shortReason =
    pref?.primary_target_reason && pref.primary_target_reason.length < 160
      ? pref.primary_target_reason
      : pe?.invalidation_reason && pe.invalidation_reason.length < 160
      ? pe.invalidation_reason
      : null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildShareText({ pair, tf, pref, pe, targets, plan }));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — ignore */ }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 }}
      className="border border-neutral-900 bg-neutral-950 p-5 sm:p-6"
      data-testid="trade-idea-card"
    >
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-neutral-900 gap-3">
        <h2 className="text-sm sm:text-base font-bold uppercase tracking-wider text-white">Preferred Trade Idea</h2>
        <button
          onClick={handleCopy}
          data-testid="copy-plan-btn"
          title="Copy plan to clipboard"
          className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] px-2.5 py-1.5 border transition-colors ${copied ? "border-[#00FF66] text-[#00FF66]" : "border-neutral-800 text-neutral-400 hover:border-white hover:text-white"}`}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "copied" : "copy plan"}
        </button>
      </div>

      {/* Trade logic one-liner */}
      <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-5 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="trade-logic-hint">
        <span>Recent Swing Low</span>
        <span className="text-neutral-700">→</span>
        <span className="text-[#00E5FF]">Entry</span>
        <span className="text-neutral-700">→</span>
        <span>Recent Swing High</span>
      </div>

      <div className="space-y-3">
        <KvRow icon={<LogIn className="w-4 h-4 text-[#00E5FF]" />} label="Entry" testid="row-entry">
          {pe.entry_zone || "—"}
        </KvRow>

        {useLadder ? (
          <div data-testid="row-targets" className="pt-1">
            <div className="flex items-center gap-2 mb-2">
              <TargetIcon className="w-4 h-4 text-[#00FF66]" />
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">Targets</span>
            </div>
            <div className="divide-y divide-neutral-900 border border-neutral-900">
              {targets.map((t, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2.5 gap-2" data-testid={`target-tp${i + 1}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 w-10 sm:w-12 flex-shrink-0">
                    TP{i + 1}
                  </span>
                  <span className="font-mono text-sm text-white flex-1 text-center truncate">
                    {t.price}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF66] w-10 flex-shrink-0 text-right">
                    {t.allocation_pct}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <KvRow icon={<TargetIcon className="w-4 h-4 text-[#00FF66]" />} label="Target" testid="row-target">
            {pref?.primary_target || "—"}
          </KvRow>
        )}

        <KvRow icon={<Octagon className="w-4 h-4 text-[#FF4757]" />} label="Stop-loss" testid="row-stop">
          {pe.hard_stop_loss || "—"}
        </KvRow>

        <KvRow label="Risk/Reward" testid="row-rr" valueClass="text-[#FFB000]">
          {pe.risk_reward || "—"}
        </KvRow>
      </div>

      {shortReason && (
        <div
          className="mt-5 pt-4 border-t border-neutral-900 text-xs sm:text-sm text-neutral-300 leading-relaxed"
          data-testid="short-reason"
        >
          {shortReason}
        </div>
      )}
    </motion.div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* NO TRADE card                                                      */
/* ────────────────────────────────────────────────────────────────── */
function NoTradeCard({ reason }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 }}
      className="border border-[#FFB000]/30 bg-[#FFB000]/[0.03] p-5 sm:p-6"
      data-testid="no-trade-card"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFB000] mb-3">reason</div>
      <p className="text-sm sm:text-base text-neutral-100 leading-relaxed" data-testid="no-trade-reason">
        {reason || "No valid recent-swing setup is available right now."}
      </p>
    </motion.div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Row primitive — stacks on mobile for long values                    */
/* ────────────────────────────────────────────────────────────────── */
function KvRow({ icon, label, children, testid, valueClass = "text-white" }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3" data-testid={testid}>
      {icon && <div className="w-5 flex-shrink-0 flex items-center justify-center">{icon}</div>}
      <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 w-20 sm:w-28 flex-shrink-0">{label}</span>
      <span className={`font-mono text-sm sm:text-base ${valueClass} flex-1 text-right truncate`} title={typeof children === "string" ? children : undefined}>
        {children}
      </span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Helpers                                                             */
/* ────────────────────────────────────────────────────────────────── */

/**
 * Extract numeric prices from the AI's string fields so the chart
 * overlay and clipboard share can use real numbers.
 *
 * The engine emits:
 *   entry_zone     → "$120.64 - $121.12"  or "$120.64 – $121.12"
 *   hard_stop_loss → "$120.64"
 *   targets[i]     → {price: "$121.36", allocation_pct: 33}
 *   primary_target → "$122.09"
 */
function buildPlanFromResult(result) {
  const pref = result?.preferred_trade_idea;
  if (!pref) return null;
  const pe = pref.preferred_entry || {};
  // V2 single-entry contract — pull only the LOW price out of entry_zone,
  // ignore any legacy upper bound. `entryHigh` is intentionally always
  // equal to `entryLow` so the chart overlay never gets two lines.
  const entryLow = parseEntryLow(pe.entry_zone);
  const entryHigh = entryLow;
  const stop = parsePrice(pe.hard_stop_loss);
  let targets = null;
  if (Array.isArray(pref.targets) && pref.targets.length) {
    targets = pref.targets
      .map((t) => ({ price: parsePrice(t.price), allocation_pct: t.allocation_pct }))
      .filter((t) => t.price != null);
  }
  const target = parsePrice(pref.primary_target);
  return { entryLow, entryHigh, stop, target, targets };
}

const INVALID_PLAN_REASON =
  "This trade plan failed price validation (entry, stop-loss or targets out of order), so it is not shown.";

function isValidLongPlan(plan, referencePrice) {
  if (!plan || plan.entryLow == null || plan.stop == null) return false;
  if (!(plan.stop > 0 && plan.stop < plan.entryLow)) return false;
  if (typeof referencePrice === "number" && plan.entryLow > referencePrice) return false;
  const tps = plan.targets && plan.targets.length ? plan.targets.map((t) => t.price) : [plan.target];
  if (tps.some((p) => p == null || p <= plan.entryLow)) return false;
  return tps.every((p, i) => i === 0 || p > tps[i - 1]);
}

function parsePrice(s) {
  if (s == null) return null;
  const m = String(s).replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

function parseEntryLow(s) {
  // V2 Beginner: a single price. We still defensively pick the LOW
  // if a legacy range ever reaches the frontend.
  if (!s) return null;
  const nums = String(s).replace(/,/g, "").match(/-?\d+(?:\.\d+)?/g);
  if (!nums || !nums.length) return null;
  return Math.min(...nums.map(parseFloat));
}

function buildShareText({ pair, tf, pref, pe, targets, plan }) {
  const lines = [];
  lines.push(`${pair || ""} ${tf ? "· " + tf : ""} — ${pref?.direction || "Long"}`.trim());
  if (pe?.entry_zone) lines.push(`Entry:     ${pe.entry_zone}`);
  if (pe?.hard_stop_loss) lines.push(`Stop-loss: ${pe.hard_stop_loss}`);
  if (Array.isArray(targets) && targets.length >= 2) {
    targets.forEach((t, i) => {
      lines.push(`TP${i + 1}:       ${t.price}  (${t.allocation_pct}%)`);
    });
  } else if (pref?.primary_target) {
    lines.push(`Target:    ${pref.primary_target}`);
  }
  if (pe?.risk_reward) lines.push(`R:R:       ${pe.risk_reward}`);
  lines.push("");
  lines.push("via ChartLenz · recent-swing V2");
  return lines.join("\n");
}
